import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import useSWR, { mutate } from 'swr';
import {
  AppConstants,
  PlatformApi,
  PlatformType,
  topLoaderProgress,
} from '../../configs';
import { AxiosAuth, useAppSelector, VRIAppStateType } from '../../core';
import {
  ApiResponse,
  ApiSchemaLanguageData,
  ApiSchemaEditLanguageData,
  ApiSchemaCreateLanguageData,
  WithPagination,
  ApiSchemaFilter,
} from '../../models';
import { OptionMapper, UtilsJQuery } from '../../utils';

type Props = {
  pageSize: number;
  pageNo: number;
  searchText?: string;
  setLoaderProgress: any;
};

let pagedLanguageUrl: string;

export function useFunctionalityPagedLanguage(props: Props) {
  const { pageSize, pageNo, searchText, setLoaderProgress } = props;

  pagedLanguageUrl = PlatformApi.Language.PagedGetAll(
    pageSize,
    pageNo,
    searchText
  );

  const { data: apiPagedLanguageData } = useSWR<{
    data: ApiSchemaLanguageData[];
    count: number;
  }>(
    pagedLanguageUrl,
    async () => {
      setLoaderProgress(topLoaderProgress.start);

      try {
        const response = await AxiosAuth.get<
          ApiResponse<WithPagination<ApiSchemaLanguageData[]>>
        >(pagedLanguageUrl);

        setLoaderProgress(topLoaderProgress.complete);
        // console.log('data:', response.data.data.data);
        return {
          data: response.data.data.data,
          count: response.data.data.count,
        };
      } catch (e) {
        setLoaderProgress(topLoaderProgress.complete);
        console.error(e);
        throw e;
      }
    }
  );

  const pagedLanguageList = useMemo(
    () => apiPagedLanguageData?.data ?? [],
    [apiPagedLanguageData]
  );

  const pagedLanguageListCount = apiPagedLanguageData?.count ?? 0;

  return {
    pagedLanguageList,
    pagedLanguageListCount,
  };
}

type languageProps = {
  plaformType?: PlatformType;
  companyId?: string;
  shouldLanguageGetAllInvoke?: boolean;
};

export function useFunctionalityLanguage(props: languageProps) {
  const { plaformType, companyId, shouldLanguageGetAllInvoke = false } = props;

  const userId = useAppSelector(
    (state: VRIAppStateType) => state.auth.profile?.id
  );
  const userRole = useAppSelector(
    (state: VRIAppStateType) => state.auth.profile?.userRole
  );
  const isLoggedIn = useAppSelector(
    (state: VRIAppStateType) => state.auth.isLoggedIn
  );

  const languageCreateUrl = PlatformApi.Language.Create();
  const languageDeleteUrl = PlatformApi.Language.Delete();

  const privateLanguageUrl = useMemo(
    () =>
      PlatformApi.Language.GetByUserId(
        userId ?? '',
        plaformType ?? PlatformType.All
      ),
    [userId, plaformType]
  );

  const publicLanguageUrl = useMemo(
    () =>
      PlatformApi.Language.GetByCompanyId(
        companyId ?? '',
        plaformType ?? PlatformType.All
      ),
    [companyId, plaformType]
  );

  const url = useMemo(() => {
    if (!!userRole && userRole !== AppConstants.UserRoles.Consumer)
      return privateLanguageUrl;
    return publicLanguageUrl;
  }, [userRole, privateLanguageUrl, publicLanguageUrl]);

  const {
    data: apiLanguageData,
    error,
    isLoading,
    isValidating,
  } = useSWR<ApiSchemaLanguageData[]>(
    isLoggedIn && shouldLanguageGetAllInvoke ? url : null,
    async () => {
      const r = await AxiosAuth.get<
        ApiResponse<WithPagination<ApiSchemaLanguageData[]>>
      >(url);
      return r.data.data.data !== undefined
        ? r.data.data.data
        : (r.data.data as any);
    },
    { revalidateOnFocus: false, shouldRetryOnError: false }
  );

  const adminLanguageList: ApiSchemaLanguageData[] = useMemo(
    () => (Array.isArray(apiLanguageData) ? apiLanguageData : []),
    [apiLanguageData]
  );

  const languageList: { value: string; label: string }[] = useMemo(
    () =>
      Array.isArray(apiLanguageData)
        ? OptionMapper.mapperFunc(apiLanguageData)
        : [],
    [apiLanguageData]
  );

  const onAdd = useCallback(
    async (form: ApiSchemaCreateLanguageData) => {
      const btnLoading = UtilsJQuery.Ladda('.login-from-submit-btn');
      btnLoading.start?.();
      // console.log('form', form);

      try {
        const response = await AxiosAuth.post(languageCreateUrl, form);

        if (response.status === AppConstants.Api.StatusOK) {
          // refresh both the simple list and the paged list
          mutate(url);
          mutate(pagedLanguageUrl);
          toast.success('Language added successfully');
        }
      } catch (e) {
        toast.error('Failed to add language.');
      } finally {
        btnLoading.stop?.();
      }

      return Promise.resolve(null);
    },
    [languageCreateUrl, url]
  );

  const onEdit = useCallback(
    async (editForm: ApiSchemaEditLanguageData, id: string) => {
      const btnLoading = UtilsJQuery.Ladda('.login-from-submit-btn');
      btnLoading.start?.();
      // console.log('form of edited', editForm);

      try {
        const response = await AxiosAuth.put(
          PlatformApi.Language.Edit(id),
          editForm
        );

        if (response.status === AppConstants.Api.StatusOK) {
          mutate(url);
          mutate(pagedLanguageUrl);
          toast.success('Language edited successfully');
        }
      } catch (e) {
        toast.error('Failed to edit language.');
      } finally {
        btnLoading.stop?.();
      }

      return Promise.resolve(null);
    },
    [url]
  );

  const onDelete = useCallback(
    async (deleteList: string[]) => {
      const btnLoading = UtilsJQuery.Ladda('.delete-button');
      btnLoading.start?.();

      try {
        const response = await AxiosAuth.delete(languageDeleteUrl, {
          data: deleteList,
        });

        if (response.status === AppConstants.Api.StatusOK) {
          toast.success('Language deleted successfully');
          mutate(url);
          mutate(pagedLanguageUrl);
        }
      } catch (e) {
        toast.error('Failed to delete language.');
        return Promise.reject(e);
      } finally {
        btnLoading.stop?.();
      }

      return Promise.resolve(null);
    },
    [languageDeleteUrl, url]
  );

  const onDeleteLanguageTerm = useCallback(async (id: string) => {
    try {
      const response = await AxiosAuth.delete(
        PlatformApi.LanguageSpokenTerm.Delete(id)
      );

      if (response.status === AppConstants.Api.StatusOK) {
        toast.success('Spoken term deleted successfully');
        mutate(pagedLanguageUrl);
      }
    } catch (e) {
      toast.error('Failed to delete spoken term.');
      return Promise.reject(e);
    }
    return Promise.resolve(null);
  }, []);

  return {
    languageList,
    adminLanguageList,
    onAdd,
    editLanguage: onEdit,
    onDelete,
    onDeleteLanguageTerm,
    isLoading,
    isValidating,
    error,
  };
}

export function useFunctionalityAllLanguages({
  shouldGetAllLanguagesInvoke = false,
} = {}) {
  const isLoggedIn = useAppSelector(
    (state: VRIAppStateType) => state.auth.isLoggedIn
  );
  const getAllLanguagesUrl = PlatformApi.Language.GetAll();

  const {
    data: apiLanguageData,
    error,
    isLoading,
    isValidating,
  } = useSWR<ApiSchemaLanguageData[]>(
    isLoggedIn && shouldGetAllLanguagesInvoke ? getAllLanguagesUrl : null,
    async () => {
      const res = await AxiosAuth.get<
        ApiResponse<
          WithPagination<ApiSchemaLanguageData[]> | ApiSchemaLanguageData[]
        >
      >(getAllLanguagesUrl);

      const payload = res.data?.data;

      if (Array.isArray(payload)) {
        return payload;
      }

      if (payload && typeof payload === "object" && "data" in payload) {
        const innerData = (payload as WithPagination<ApiSchemaLanguageData[]>)
          .data;
        return Array.isArray(innerData) ? innerData : [];
      }

      return [];
    },
    { revalidateOnFocus: false, shouldRetryOnError: false }
  );

  const allLanguages = useMemo(
    () => (Array.isArray(apiLanguageData) ? apiLanguageData : []),
    [apiLanguageData]
  );

  const allLanguageOptions = useMemo(
    () => OptionMapper.mapperFunc(allLanguages),
    [allLanguages]
  );

  return {
    allLanguages,
    allLanguageOptions,
    isLoading,
    isValidating,
    error,
  };
}

export function useFunctionalityLanguageListNames() {
  const userId = useAppSelector(
    (state: VRIAppStateType) => state.auth.profile?.id
  );

  const onLanguageListName = useCallback(async (plaformType?: PlatformType) => {
    const privateLanguageUrl = PlatformApi.Language.GetByUserId(
      userId ?? '',
      plaformType ?? PlatformType.All
    );
    try {
      const res = await AxiosAuth.get<ApiResponse<any>>(privateLanguageUrl);

      if (res.status === AppConstants.Api.StatusOK) {
        // mutate(companyProfileDetailsUrl);
      }
      return Promise.resolve(res.data);
    } catch (e) {
      return Promise.reject(null);
    }
  }, [userId]);

  return {
    onLanguageListName,
  };
}

type SpokenTermsProps = {
  pageSize: number;
  pageNo: number;
  searchText?: string;
  setLoaderProgress?: any;
  filterData?: ApiSchemaFilter;
  filterDataChange?: number;
};

export function useFunctionalityLanguageSpokenTerms(props: SpokenTermsProps) {
  const { pageSize, pageNo, searchText, setLoaderProgress, filterData, filterDataChange } = props;

  const callTrackingUrl = useMemo(
    () =>
      PlatformApi.LanguageSpokenTerm.GetAllCallTracking(
        pageSize,
        pageNo,
        searchText?.trim(),
        filterDataChange
      ),
    [pageNo, pageSize, searchText, filterDataChange]
  );

  const {
    data: apiSpokenTerms,
    error,
    isLoading,
    mutate,
  } = useSWR<{ data: any[]; count: number }>(
    callTrackingUrl,
    async () => {
      setLoaderProgress?.(topLoaderProgress.start);
      try {
        const res = await AxiosAuth.post<
          ApiResponse<WithPagination<any[]> | any[]>
        >(callTrackingUrl, filterData);

        const payload = res?.data?.data;

        if (payload && typeof payload === "object" && "data" in payload) {
          const innerData = (payload as any).data;
          const count = (payload as any).count;

          return {
            data: Array.isArray(innerData) ? innerData : [],
            count:
              typeof count === "number"
                ? count
                : Array.isArray(innerData)
                ? innerData.length
                : 0,
          };
        }

        if (Array.isArray(payload)) {
          return { data: payload, count: payload.length };
        }

        return { data: [], count: 0 };
      } catch (e) {
        throw e;
      } finally {
        setLoaderProgress?.(topLoaderProgress.complete);
      }
    },
    { revalidateOnFocus: false, shouldRetryOnError: false }
  );

  const spokenTerms = useMemo(
    () => (apiSpokenTerms?.data && Array.isArray(apiSpokenTerms.data) ? apiSpokenTerms.data : []),
    [apiSpokenTerms]
  );

  const spokenTermsCount = apiSpokenTerms?.count ?? 0;

  return {
    spokenTerms,
    spokenTermsCount,
    isLoading,
    error,
    mutate,
  };
}


export function useFunctionalityLanguageByCompanyId(props: languageProps) {
  const { plaformType, companyId } = props;
  const isLoggedIn = useAppSelector(
    (state: VRIAppStateType) => state.auth.isLoggedIn
  );

  const publicLanguageUrl = PlatformApi.Language.GetByCompanyId(
    companyId ?? '',
    plaformType ?? PlatformType.All
  );

  const { data: apiLanguageData } = useSWR<ApiSchemaLanguageData[]>(
    isLoggedIn && companyId ? publicLanguageUrl : null,
    () =>
      AxiosAuth.get<ApiResponse<WithPagination<ApiSchemaLanguageData[]>>>(
        publicLanguageUrl
      )
        .then((r) =>
          r.data.data.data !== undefined ? r.data.data.data : r.data.data
        )
        .catch((e) => {
          console.error(e);
          return e;
        })
  );

  const languageList = useMemo(() => {
    const data = Array.isArray(apiLanguageData)
      ? OptionMapper.mapperFunc(apiLanguageData)
      : [];
    return data;
  }, [apiLanguageData]);

  return {
    languageList,
  };
}
